import { ref, onMounted, onUnmounted } from "vue";
import { onBeforeRouteLeave } from "vue-router";

/**
 * Composable for tracking unsaved changes in forms
 * @param {Ref} dataRef - The reactive data object to track
 * @param {Object} options - Configuration options
 * @returns {Object} - Methods and state for tracking unsaved changes
 */
export function useUnsavedChanges(dataRef, options = {}) {
  const {
    confirmMessage = "You have unsaved changes. Are you sure you want to leave this page? Your changes will be lost.",
    enableBrowserWarning = true,
    enableRouterGuard = true,
    usePendingOperations = false,
  } = options;

  // State tracking
  const hasUnsavedChanges = ref(false);
  const lastSavedState = ref(null);
  const isInitialLoading = ref(false);
  const pendingAsyncOperations = ref(0);

  // Track changes to mark form as dirty
  const markAsChanged = () => {
    // Don't mark as changed during initial loading
    if (isInitialLoading.value) {
      return;
    }

    // Don't mark as changed while async operations are pending (if enabled)
    if (usePendingOperations && pendingAsyncOperations.value > 0) {
      return;
    }

    // Only mark as changed if not already marked
    if (!hasUnsavedChanges.value) {
      hasUnsavedChanges.value = true;
    }
  };

  // Mark form as saved
  const markAsSaved = () => {
    hasUnsavedChanges.value = false;
    lastSavedState.value = JSON.stringify(dataRef.value);
  };

  // Check if current state differs from last saved state
  const checkForChanges = () => {
    if (!lastSavedState.value) {
      return hasUnsavedChanges.value;
    }

    const currentState = JSON.stringify(dataRef.value);
    return currentState !== lastSavedState.value;
  };

  // Reset the unsaved changes state
  const resetChanges = () => {
    hasUnsavedChanges.value = false;
    lastSavedState.value = null;
  };

  // Set initial loading state
  const setInitialLoading = (loading) => {
    isInitialLoading.value = loading;
  };

  // Increment pending async operations
  const incrementPendingOperations = () => {
    if (usePendingOperations) {
      pendingAsyncOperations.value++;
    }
  };

  // Decrement pending async operations
  const decrementPendingOperations = () => {
    if (usePendingOperations && pendingAsyncOperations.value > 0) {
      pendingAsyncOperations.value--;
    }
  };

  // Beforeunload event handler for tab/browser close
  const beforeUnloadHandler = (event) => {
    if (checkForChanges() || hasUnsavedChanges.value) {
      event.preventDefault();
      event.returnValue = ""; // Chrome requires returnValue to be set
      return ""; // For other browsers
    }
  };

  // Setup lifecycle hooks
  onMounted(() => {
    if (enableBrowserWarning) {
      window.addEventListener("beforeunload", beforeUnloadHandler);
    }
  });

  onUnmounted(() => {
    if (enableBrowserWarning) {
      window.removeEventListener("beforeunload", beforeUnloadHandler);
    }
  });

  // Router guard for route navigation
  if (enableRouterGuard) {
    onBeforeRouteLeave((to, from, next) => {
      if (checkForChanges() || hasUnsavedChanges.value) {
        const answer = window.confirm(confirmMessage);
        if (answer) {
          next();
        } else {
          next(false);
        }
      } else {
        next();
      }
    });
  }

  return {
    // State
    hasUnsavedChanges,
    lastSavedState,
    isInitialLoading,
    pendingAsyncOperations,

    // Methods
    markAsChanged,
    markAsSaved,
    checkForChanges,
    resetChanges,
    setInitialLoading,
    incrementPendingOperations,
    decrementPendingOperations,
  };
}
